import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  X,
  Send,
  Paperclip,
  Image as ImageIcon,
  CheckCircle2,
  AlertTriangle,
  ArrowRight,
  Loader2,
  FileText,
  Printer,
  ExternalLink,
  Package,
  Plane,
  Truck,
  Ship,
  Check,
  Edit2,
  RefreshCw,
  Thermometer,
  Boxes,
  HelpCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const PREDEFINED_TASKS = [
  {
    id: 'extract-docket',
    label: '📋 Create Docket from Invoice/Screenshot',
    prompt: 'Please extract all shipment details from this document and prepare a Docket preview card for confirmation.'
  },
  {
    id: 'draft-booking',
    label: '📦 Draft Booking Inquiry',
    prompt: 'Draft a new Booking Request inquiry for this shipment with route, customer, and equipment specs.'
  },
  {
    id: 'calc-cbm',
    label: '📐 Calculate CBM & Volumetric Weight',
    prompt: 'Calculate the total cubic meters (CBM) and chargeable volumetric weight for: 10 boxes, 60x40x50 cm, 25 kg each.'
  },
  {
    id: 'cold-chain',
    label: '🌡️ Cold-Chain Pharma Temp Rules',
    prompt: 'What are the packaging and data logger requirements for shipping +2°C to +8°C vaccine cold-chain cargo?'
  },
  {
    id: 'ocean-fcl',
    label: '🚢 Ocean FCL Container Specs',
    prompt: 'Explain the payload weight limits and cubic capacity differences between 20’ GP, 40’ GP, and 40’ High Cube containers.'
  }
];

// Formatted Chat Message with real Markdown parsing & inline entity linking
function FormattedChatMessage({ text, onNavigate, onClose }) {
  if (!text) return null;
  const lines = text.split('\n');

  const renderInline = (str, keyPrefix = 'inline') => {
    // Regex matching: **bold**, `code`, EST-..., Dockets, Bookings, Trips
    const regex = /(\*\*.*?\*\*|`.*?`|EST-\d{4,10}[/-]?\d{1,6}|(?:STAVAN|SLPL|SILLP)[/-]\d{2,6}|(?:INQ|BK|REQ)[/-]\d{1,8}|TRIP[/-]\d+)/g;
    const parts = str.split(regex);

    return parts.map((part, idx) => {
      const k = `${keyPrefix}-${idx}`;
      if (!part) return null;

      // Bold: **text**
      if (part.startsWith('**') && part.endsWith('**') && part.length >= 4) {
        const inner = part.slice(2, -2);
        return <strong key={k} className="font-bold text-slate-900">{renderInline(inner, `${k}-b`)}</strong>;
      }

      // Inline code: `text`
      if (part.startsWith('`') && part.endsWith('`') && part.length >= 2) {
        const inner = part.slice(1, -1);
        return <code key={k} className="bg-slate-100 text-purple-700 font-mono text-[11px] px-1 py-0.5 rounded border border-slate-200">{inner}</code>;
      }

      // Estimate Link: EST-...
      if (/^EST-\d{4,10}[/-]?\d{1,6}$/i.test(part)) {
        return (
          <button
            key={k}
            type="button"
            onClick={() => {
              onClose?.();
              onNavigate?.('/estimates');
            }}
            title="Click to view Estimates"
            className="inline-flex items-center gap-1 font-mono font-bold text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 px-1.5 py-0.5 rounded text-[11px] mx-0.5 transition cursor-pointer underline hover:no-underline shadow-2xs"
          >
            {part} <ExternalLink className="w-2.5 h-2.5 inline" />
          </button>
        );
      }

      // Docket Link: STAVAN/..., SLPL-..., SILLP-...
      if (/^(?:STAVAN|SLPL|SILLP)[/-]\d{2,6}$/i.test(part)) {
        return (
          <button
            key={k}
            type="button"
            onClick={() => {
              onClose?.();
              onNavigate?.('/dockets');
            }}
            title="Click to view Dockets"
            className="inline-flex items-center gap-1 font-mono font-bold text-purple-800 bg-purple-50 hover:bg-purple-100 border border-purple-300 px-1.5 py-0.5 rounded text-[11px] mx-0.5 transition cursor-pointer underline hover:no-underline shadow-2xs"
          >
            {part} <ExternalLink className="w-2.5 h-2.5 inline" />
          </button>
        );
      }

      // Booking Link: INQ-..., BK-...
      if (/^(?:INQ|BK|REQ)[/-]\d{1,8}$/i.test(part)) {
        return (
          <button
            key={k}
            type="button"
            onClick={() => {
              onClose?.();
              onNavigate?.('/booking');
            }}
            title="Click to view Bookings"
            className="inline-flex items-center gap-1 font-mono font-bold text-blue-800 bg-blue-50 hover:bg-blue-100 border border-blue-300 px-1.5 py-0.5 rounded text-[11px] mx-0.5 transition cursor-pointer underline hover:no-underline shadow-2xs"
          >
            {part} <ExternalLink className="w-2.5 h-2.5 inline" />
          </button>
        );
      }

      // Trip Link: TRIP-...
      if (/^TRIP[/-]\d+$/i.test(part)) {
        return (
          <button
            key={k}
            type="button"
            onClick={() => {
              onClose?.();
              onNavigate?.('/fleet/trips');
            }}
            title="Click to view Fleet Trips"
            className="inline-flex items-center gap-1 font-mono font-bold text-amber-800 bg-amber-50 hover:bg-amber-100 border border-amber-300 px-1.5 py-0.5 rounded text-[11px] mx-0.5 transition cursor-pointer underline hover:no-underline shadow-2xs"
          >
            {part} <ExternalLink className="w-2.5 h-2.5 inline" />
          </button>
        );
      }

      return part;
    });
  };

  return (
    <div className="space-y-1.5 text-xs font-ibm leading-relaxed">
      {lines.map((line, idx) => {
        const trimmed = line.trim();

        if (!trimmed) {
          return <div key={idx} className="h-1" />;
        }

        // Heading 3: ### ...
        if (trimmed.startsWith('### ')) {
          return (
            <h3 key={idx} className="text-xs font-bold text-slate-900 mt-2.5 mb-1 pb-1 border-b border-gray-100 flex items-center gap-1.5">
              {renderInline(trimmed.replace('### ', ''), `h3-${idx}`)}
            </h3>
          );
        }

        // Heading 4: #### ...
        if (trimmed.startsWith('#### ')) {
          return (
            <h4 key={idx} className="text-[11.5px] font-bold text-slate-800 mt-2 mb-0.5">
              {renderInline(trimmed.replace('#### ', ''), `h4-${idx}`)}
            </h4>
          );
        }

        // Sub-bullet with 2 or 4 spaces: "  - " or "    - "
        if (/^\s{2,}[-*]\s/.test(line)) {
          return (
            <div key={idx} className="flex items-start gap-1.5 ml-4 text-[11.5px] text-slate-700 my-0.5">
              <span className="text-slate-400 font-bold">•</span>
              <span className="flex-1">{renderInline(trimmed.replace(/^[-*]\s+/, ''), `subli-${idx}`)}</span>
            </div>
          );
        }

        // Bullet point: "- " or "* "
        if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
          return (
            <div key={idx} className="flex items-start gap-1.5 ml-1 text-slate-800 my-0.5">
              <span className="text-purple-500 font-bold">•</span>
              <span className="flex-1">{renderInline(trimmed.replace(/^[-*]\s+/, ''), `li-${idx}`)}</span>
            </div>
          );
        }

        // Numbered item: "1. ", "2. ", etc.
        const numMatch = trimmed.match(/^(\d+)\.\s+(.*)$/);
        if (numMatch) {
          return (
            <div key={idx} className="flex items-start gap-1.5 ml-1 text-slate-800 my-0.5">
              <span className="text-purple-600 font-bold min-w-[14px]">{numMatch[1]}.</span>
              <span className="flex-1">{renderInline(numMatch[2], `num-${idx}`)}</span>
            </div>
          );
        }

        // Normal paragraph line
        return (
          <p key={idx} className="my-0.5">
            {renderInline(trimmed, `p-${idx}`)}
          </p>
        );
      })}
    </div>
  );
}

// Helper to extract current user login profile
function getCurrentUserProfile() {
  try {
    const raw = localStorage.getItem('user_profile');
    if (raw) {
      const u = JSON.parse(raw);
      return {
        id: String(u.id || u.email || u.username || 'default_user'),
        name: u.first_name ? `${u.first_name} ${u.last_name || ''}`.trim() : String(u.name || u.username || u.email || 'User')
      };
    }
    const token = localStorage.getItem('auth_token');
    if (token) {
      const base64Url = token.split('.')[1];
      if (base64Url) {
        const json = JSON.parse(atob(base64Url));
        return {
          id: String(json.sub || json.user_id || 'default_user'),
          name: String(json.sub || json.email || 'User')
        };
      }
    }
  } catch (e) {
    // ignore
  }
  return { id: 'default_user', name: 'User' };
}

export default function AiDocketAssistantModal({ isOpen, onClose, onDocketCreated }) {
  const navigate = useNavigate();

  const userProfile = useMemo(() => getCurrentUserProfile(), []);
  const storageKey = useMemo(() => `ai_copilot_messages_${userProfile.id}`, [userProfile.id]);

  const defaultWelcome = useMemo(() => [
    {
      id: 'welcome',
      role: 'assistant',
      text: `Hello ${userProfile.name}! I am your AI Logistics Copilot powered by GPT-4o Vision. You can paste an invoice or email screenshot (Ctrl+V), upload documents, or ask any operational question across Dockets, Estimates, Bookings, and Fleet.`,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ], [userProfile.name]);

  // User-specific chat message stream (persisted per logged-in user)
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      }
    } catch (e) {
      console.warn('Failed to parse saved user copilot messages:', e);
    }
    return defaultWelcome;
  });

  // Re-sync messages when modal opens or user login changes
  useEffect(() => {
    if (!isOpen) return;
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setMessages(parsed);
          return;
        }
      }
    } catch (e) {
      // ignore
    }
    setMessages(defaultWelcome);
  }, [isOpen, storageKey, defaultWelcome]);

  // Persist chat stream to user-specific localStorage key
  useEffect(() => {
    try {
      const sanitized = messages.map(m => {
        if (m.file && m.file.preview && m.file.preview.startsWith('data:')) {
          return { ...m, file: { name: m.file.name, size: m.file.size } };
        }
        return m;
      });
      localStorage.setItem(storageKey, JSON.stringify(sanitized));
    } catch (e) {
      console.warn('Failed to save user chat to localStorage:', e);
    }
  }, [messages, storageKey]);

  const [inputPrompt, setInputPrompt] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [editingCardId, setEditingCardId] = useState(null);

  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Auto-scroll to bottom
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, analyzing, scrollToBottom]);

  // Global paste listener (Ctrl+V) when modal is open
  useEffect(() => {
    if (!isOpen) return;

    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.indexOf('image') !== -1) {
          const file = item.getAsFile();
          if (file) {
            setSelectedFile(file);
            setPreviewImageUrl(URL.createObjectURL(file));
            textareaRef.current?.focus();
            break;
          }
        }
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [isOpen]);

  if (!isOpen) return null;

  // File selection
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      if (file.type.startsWith('image/')) {
        setPreviewImageUrl(URL.createObjectURL(file));
      } else {
        setPreviewImageUrl(null);
      }
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setPreviewImageUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Predefined task click
  const handleSelectTask = (task) => {
    setInputPrompt(task.prompt);
    textareaRef.current?.focus();
  };

  // Send message
  const handleSendMessage = async (customPrompt) => {
    const promptToSend = (customPrompt || inputPrompt).trim();
    if (!promptToSend && !selectedFile) return;

    const fileToUpload = selectedFile;
    const filePreview = previewImageUrl;

    // Append user message
    const userMsgId = String(Date.now());
    const newMessages = [
      ...messages,
      {
        id: userMsgId,
        role: 'user',
        text: promptToSend,
        file: fileToUpload ? { name: fileToUpload.name, preview: filePreview } : null,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }
    ];

    setMessages(newMessages);
    setInputPrompt('');
    setSelectedFile(null);
    setPreviewImageUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setAnalyzing(true);

    try {
      const token = localStorage.getItem('auth_token');

      // Document extraction should ONLY trigger if:
      // 1. A file is actually attached (image/pdf)
      // 2. OR prompt begins with predefined task "📋"
      // 3. OR prompt explicitly begins with an extraction directive
      const isDocumentExtraction = !!fileToUpload ||
        promptToSend.startsWith('📋') ||
        (
          (promptToSend.toLowerCase().startsWith('create docket from') || promptToSend.toLowerCase().startsWith('extract from')) &&
          !promptToSend.toLowerCase().includes('pending') &&
          !promptToSend.toLowerCase().includes('what is') &&
          !promptToSend.toLowerCase().includes('already')
        );

      if (isDocumentExtraction) {
        const formData = new FormData();
        if (promptToSend) formData.append('text', promptToSend);
        if (fileToUpload) formData.append('file', fileToUpload);

        const res = await fetch(`${API}/api/ai/extract-docket-intent`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });

        if (res.ok) {
          const previewData = await res.json();
          setMessages(prev => [
            ...prev,
            {
              id: String(Date.now()),
              role: 'assistant',
              text: previewData.summary || 'I have extracted the shipment parameters from your request. Please review the details below:',
              previewCard: previewData,
              timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            }
          ]);
        } else {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || 'Failed to extract shipment parameters');
        }
      } else {
        // Conversational Copilot Chat
        const res = await fetch(`${API}/api/ai/copilot-chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ message: promptToSend })
        });

        if (res.ok) {
          const data = await res.json();
          setMessages(prev => [
            ...prev,
            {
              id: String(Date.now()),
              role: 'assistant',
              text: data.reply || 'Here is what I found for your request.',
              context: data.context || null,
              timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            }
          ]);
        } else {
          throw new Error('Copilot chat error');
        }
      }
    } catch (err) {
      console.error('AI Assistant Error:', err);
      setMessages(prev => [
        ...prev,
        {
          id: String(Date.now()),
          role: 'assistant',
          isError: true,
          text: `⚠️ ${err.message || 'Error communicating with AI service. Please check API settings.'}`,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
      ]);
    } finally {
      setAnalyzing(false);
    }
  };

  // 1-Click Confirm & Create Docket
  const handleConfirmCreateDocket = async (cardData, messageId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API}/api/ai/confirm-create-docket`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(cardData)
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to create docket');
      }

      const created = await res.json();
      setMessages(prev => prev.map(msg => {
        if (msg.id === messageId) {
          return {
            ...msg,
            previewCard: null,
            confirmedDocket: created,
            text: `✅ **Docket ${created.docket_number}** created successfully! Dedicated discussion channel created in Team Chat.`
          };
        }
        return msg;
      }));

      if (onDocketCreated) onDocketCreated(created);
    } catch (err) {
      alert(err.message || 'Failed to create docket');
    }
  };

  // 1-Click Save as Booking Inquiry
  const handleSaveAsBooking = async (cardData, messageId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API}/api/ai/confirm-create-booking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(cardData)
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save booking inquiry');
      }

      const created = await res.json();
      setMessages(prev => prev.map(msg => {
        if (msg.id === messageId) {
          return {
            ...msg,
            previewCard: null,
            confirmedBooking: created,
            text: `📦 **Booking Inquiry ${created.inquiry_number}** saved successfully to Pending Bookings!`
          };
        }
        return msg;
      }));
    } catch (err) {
      alert(err.message || 'Failed to save booking inquiry');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-5 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-[90vh] max-h-[850px] overflow-hidden border border-gray-100 flex flex-col">
        {/* ─── Header ─── */}
        <div className="px-5 py-3.5 bg-gradient-to-r from-purple-900 via-indigo-900 to-slate-900 text-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-purple-600/90 shadow-md flex items-center justify-center text-white ring-2 ring-purple-400/30">
              <Sparkles className="w-5 h-5 text-purple-200" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-sm tracking-wide font-manrope">AI Logistics Assistant</h3>
                <span className="text-[10px] font-mono font-bold bg-purple-500/30 text-purple-200 border border-purple-400/30 px-2 py-0.5 rounded-full">
                  GPT-4o Vision & Copilot
                </span>
              </div>
              <p className="text-[11px] text-purple-200/90 font-ibm">
                Paste screenshots (Ctrl+V) or chat in plain English to generate Dockets and Bookings
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                const cleared = [
                  {
                    id: 'welcome',
                    role: 'assistant',
                    text: `Chat cleared for ${userProfile.name}. How can I assist you with your shipments or estimates today?`,
                    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                  }
                ];
                setMessages(cleared);
                localStorage.removeItem(storageKey);
              }}
              title="Clear chat history"
              className="px-2.5 py-1 text-xs text-purple-200 hover:text-white hover:bg-white/10 rounded-lg transition-colors font-ibm cursor-pointer"
            >
              Clear
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/10 text-purple-200 hover:text-white transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* ─── Predefined Task Quick Pills ─── */}
        <div className="px-5 py-2.5 bg-slate-50 border-b border-gray-100 flex items-center gap-2 overflow-x-auto shrink-0 scrollbar-none">
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider font-ibm shrink-0 flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-purple-600" /> Tasks:
          </span>
          {PREDEFINED_TASKS.map(task => (
            <button
              key={task.id}
              onClick={() => handleSelectTask(task)}
              className="text-xs font-medium font-ibm bg-white hover:bg-purple-50 text-slate-700 hover:text-purple-700 border border-gray-200 hover:border-purple-200 px-3 py-1 rounded-full whitespace-nowrap transition-all shadow-2xs cursor-pointer"
            >
              {task.label}
            </button>
          ))}
        </div>

        {/* ─── Chat Message Stream ─── */}
        <div className="flex-1 p-5 overflow-y-auto space-y-4 bg-slate-50/40">
          {messages.map(msg => (
            <div
              key={msg.id}
              className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}
            >
              <div className="flex items-start gap-2.5 max-w-[88%]">
                {msg.role === 'assistant' && (
                  <div className="w-7 h-7 rounded-lg bg-purple-600 text-white flex items-center justify-center shrink-0 mt-0.5 shadow-xs">
                    <Sparkles className="w-3.5 h-3.5" />
                  </div>
                )}

                <div className="space-y-2">
                  {/* Attached user file thumbnail */}
                  {msg.file && (
                    <div className="bg-white p-2 rounded-xl border border-gray-200 shadow-2xs max-w-xs">
                      {msg.file.preview ? (
                        <img
                          src={msg.file.preview}
                          alt="Screenshot"
                          className="rounded-lg max-h-40 object-cover w-full cursor-pointer hover:opacity-95"
                          onClick={() => window.open(msg.file.preview, '_blank')}
                        />
                      ) : (
                        <div className="flex items-center gap-2 text-xs font-mono text-slate-700">
                          <FileText className="w-4 h-4 text-purple-600" />
                          <span className="truncate">{msg.file.name}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Message bubble */}
                  {msg.text && (
                    <div
                      className={`p-3.5 rounded-2xl text-xs font-ibm leading-relaxed shadow-2xs ${
                        msg.role === 'user'
                          ? 'bg-purple-600 text-white rounded-br-xs'
                          : msg.isError
                          ? 'bg-rose-50 text-rose-800 border border-rose-200 rounded-bl-xs'
                          : 'bg-white text-slate-800 border border-gray-200/90 rounded-bl-xs'
                      }`}
                    >
                      {msg.role === 'user' ? (
                        <div className="whitespace-pre-wrap">{msg.text}</div>
                      ) : (
                        <FormattedChatMessage text={msg.text} onNavigate={navigate} onClose={onClose} />
                      )}

                      {/* Live ERP Entity Navigation Shortcuts */}
                      {/* Estimates */}
                      {msg.context?.matched_estimates?.map(e => (
                        <div key={e.id || e.estimate_no} className="mt-3 pt-2.5 border-t border-emerald-100 flex items-center justify-between gap-3 flex-wrap">
                          <span className="text-[11px] font-mono font-bold text-emerald-900 flex items-center gap-1.5">
                            <FileText className="w-3.5 h-3.5 text-emerald-600" />
                            {e.estimate_no} ({e.currency} {Number(e.total_amount || 0).toLocaleString()} · {e.status})
                          </span>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                onClose();
                                navigate('/estimates');
                              }}
                              className="text-[11px] font-bold text-emerald-700 hover:text-emerald-900 underline flex items-center gap-1 cursor-pointer"
                            >
                              Open in Estimates <ExternalLink className="w-3 h-3" />
                            </button>
                            {e.id && (
                              <button
                                onClick={() => {
                                  window.open(`/estimates/print/${e.id}`, '_blank');
                                }}
                                className="text-[11px] font-bold text-slate-600 hover:text-slate-900 flex items-center gap-1 cursor-pointer bg-slate-100 hover:bg-slate-200 px-2 py-0.5 rounded"
                              >
                                <Printer className="w-3 h-3" /> Print PDF
                              </button>
                            )}
                          </div>
                        </div>
                      ))}

                      {/* Dockets */}
                      {msg.context?.matched_dockets?.map(d => (
                        <div key={d.system_docket_no} className="mt-3 pt-2.5 border-t border-purple-100 flex items-center justify-between gap-3 flex-wrap">
                          <span className="text-[11px] font-mono font-bold text-purple-900 flex items-center gap-1.5">
                            <FileText className="w-3.5 h-3.5 text-purple-600" />
                            {d.system_docket_no} ({d.status})
                          </span>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                onClose();
                                navigate('/dockets');
                              }}
                              className="text-[11px] font-bold text-purple-700 hover:text-purple-900 underline flex items-center gap-1 cursor-pointer"
                            >
                              Open in Dockets <ExternalLink className="w-3 h-3" />
                            </button>
                            {d.id && (
                              <button
                                onClick={() => {
                                  window.open(`/dockets/print/${d.id}`, '_blank');
                                }}
                                className="text-[11px] font-bold text-slate-600 hover:text-slate-900 flex items-center gap-1 cursor-pointer bg-slate-100 hover:bg-slate-200 px-2 py-0.5 rounded"
                              >
                                <Printer className="w-3 h-3" /> Print
                              </button>
                            )}
                          </div>
                        </div>
                      ))}

                      {/* Bookings */}
                      {msg.context?.matched_bookings?.map(b => (
                        <div key={b.inquiry_number} className="mt-3 pt-2.5 border-t border-blue-100 flex items-center justify-between gap-3 flex-wrap">
                          <span className="text-[11px] font-mono font-bold text-blue-900 flex items-center gap-1.5">
                            <Package className="w-3.5 h-3.5 text-accent-blue" />
                            {b.inquiry_number} ({b.status})
                          </span>
                          <button
                            onClick={() => {
                              onClose();
                              navigate('/booking');
                            }}
                            className="text-[11px] font-bold text-accent-blue hover:text-blue-900 underline flex items-center gap-1 cursor-pointer"
                          >
                            Open in Bookings <ExternalLink className="w-3 h-3" />
                          </button>
                        </div>
                      ))}

                      {/* Trips */}
                      {msg.context?.matched_trips?.map(tr => (
                        <div key={tr.id || tr.trip_number} className="mt-3 pt-2.5 border-t border-amber-100 flex items-center justify-between gap-3 flex-wrap">
                          <span className="text-[11px] font-mono font-bold text-amber-900 flex items-center gap-1.5">
                            <Truck className="w-3.5 h-3.5 text-amber-600" />
                            Trip {tr.trip_number} ({tr.vehicle_registration} · {tr.status})
                          </span>
                          <button
                            onClick={() => {
                              onClose();
                              navigate('/fleet/trips');
                            }}
                            className="text-[11px] font-bold text-amber-700 hover:text-amber-900 underline flex items-center gap-1 cursor-pointer"
                          >
                            Open Fleet Trips <ExternalLink className="w-3 h-3" />
                          </button>
                        </div>
                      ))}

                      {/* Customers */}
                      {msg.context?.matched_customers?.map(c => (
                        <div key={c.id} className="mt-3 pt-2.5 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap">
                          <span className="text-[11px] font-mono font-bold text-slate-800 flex items-center gap-1.5">
                            <Boxes className="w-3.5 h-3.5 text-slate-600" />
                            {c.business_name} ({c.customer_type || 'Customer'})
                          </span>
                          <button
                            onClick={() => {
                              onClose();
                              navigate(`/customers/view/${c.id}`);
                            }}
                            className="text-[11px] font-bold text-slate-700 hover:text-slate-900 underline flex items-center gap-1 cursor-pointer"
                          >
                            View Customer <ExternalLink className="w-3 h-3" />
                          </button>
                        </div>
                      ))}                    </div>
                  )}

                  {/* ─── Interactive Option B Preview Card ─── */}
                  {msg.previewCard && (
                    <div className="bg-white rounded-xl border border-purple-200 shadow-md p-4 space-y-3.5 max-w-xl animate-in fade-in zoom-in-95 duration-200">
                      <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                          <h4 className="font-bold text-xs font-manrope text-slate-900 uppercase tracking-wide">
                            AI Extracted Shipment Parameters
                          </h4>
                        </div>
                        <span className="text-[10px] font-bold font-mono px-2 py-0.5 rounded bg-purple-100 text-purple-800 border border-purple-200">
                          {msg.previewCard.shipment_type || 'AIR'} FREIGHT
                        </span>
                      </div>

                      {/* Extracted Details Grid */}
                      <div className="grid grid-cols-2 gap-3 text-xs font-ibm">
                        {/* Shipper */}
                        <div className="p-2.5 bg-slate-50 rounded-lg border border-gray-100">
                          <span className="text-[10px] font-bold text-slate-500 uppercase block mb-0.5">Shipper (Exporter)</span>
                          <div className="font-bold text-slate-800 truncate">{msg.previewCard.shipper_name || '—'}</div>
                          <div className="text-[11px] text-slate-500 truncate">{msg.previewCard.shipper_address || ''}</div>
                        </div>

                        {/* Consignee */}
                        <div className="p-2.5 bg-slate-50 rounded-lg border border-gray-100">
                          <span className="text-[10px] font-bold text-slate-500 uppercase block mb-0.5">Consignee (Importer)</span>
                          <div className="font-bold text-slate-800 truncate">{msg.previewCard.consignee_name || '—'}</div>
                          <div className="text-[11px] text-slate-500 truncate">{msg.previewCard.consignee_address || ''}</div>
                        </div>

                        {/* Route */}
                        <div className="p-2.5 bg-slate-50 rounded-lg border border-gray-100">
                          <span className="text-[10px] font-bold text-slate-500 uppercase block mb-0.5">Routing</span>
                          <div className="font-bold text-slate-800">
                            {msg.previewCard.origin_city || 'Origin'} ➔ {msg.previewCard.destination_city || 'Destination'}
                          </div>
                        </div>

                        {/* Cargo & Weights */}
                        <div className="p-2.5 bg-slate-50 rounded-lg border border-gray-100">
                          <span className="text-[10px] font-bold text-slate-500 uppercase block mb-0.5">Weight & Packages</span>
                          <div className="font-bold text-slate-800">
                            {msg.previewCard.pieces || 1} Pkgs · {msg.previewCard.gross_weight || 0} Kg
                          </div>
                          {msg.previewCard.chargeable_weight && (
                            <div className="text-[10px] text-accent-blue font-semibold">
                              Chg Wt: {msg.previewCard.chargeable_weight} Kg
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Temperature or HazMat badge if applicable */}
                      {(msg.previewCard.temperature_range || msg.previewCard.is_dangerous_goods) && (
                        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs font-ibm text-amber-900">
                          <Thermometer className="w-4 h-4 text-amber-700 shrink-0" />
                          <span>
                            <strong>Temp Specs:</strong> {msg.previewCard.temperature_range || 'Standard'}
                            {msg.previewCard.is_dangerous_goods ? ' · ⚠️ Hazardous Material' : ''}
                          </span>
                        </div>
                      )}

                      {/* Attached Document Storage Link */}
                      {msg.previewCard.original_document_url && (
                        <div className="flex items-center justify-between bg-purple-50/60 border border-purple-100 rounded-lg px-3 py-1.5 text-[11px] font-ibm text-purple-900">
                          <span className="flex items-center gap-1.5">
                            <FileText className="w-3.5 h-3.5 text-purple-600" />
                            Stored in server: <strong className="font-mono text-[10px]">{msg.previewCard.original_document_name || 'Document image'}</strong>
                          </span>
                          <a
                            href={`${API}${msg.previewCard.original_document_url}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-purple-700 hover:text-purple-900 font-bold underline flex items-center gap-1"
                          >
                            View Stored File <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex items-center gap-2.5 pt-1">
                        <button
                          type="button"
                          onClick={() => handleConfirmCreateDocket(msg.previewCard, msg.id)}
                          className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-bold font-ibm text-xs py-2 px-3 rounded-lg flex items-center justify-center gap-1.5 shadow-sm transition-all cursor-pointer"
                        >
                          <Check className="w-4 h-4" />
                          Confirm & Create Docket
                        </button>

                        <button
                          type="button"
                          onClick={() => handleSaveAsBooking(msg.previewCard, msg.id)}
                          className="bg-accent-blue/10 hover:bg-accent-blue/20 text-accent-blue border border-accent-blue/30 font-bold font-ibm text-xs py-2 px-3 rounded-lg flex items-center justify-center gap-1.5 transition-all cursor-pointer"
                        >
                          <Package className="w-4 h-4" />
                          Save as Booking
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Confirmed Docket Success Banner */}
                  {msg.confirmedDocket && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3.5 flex items-center justify-between text-xs font-ibm text-emerald-950">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                        <div>
                          <div className="font-bold">System Docket #{msg.confirmedDocket.docket_number}</div>
                          <div className="text-[11px] text-emerald-700">Official docket created and linked to team chat</div>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          onClose();
                          navigate('/dockets');
                        }}
                        className="bg-emerald-600 text-white font-bold px-3 py-1.5 rounded-lg hover:bg-emerald-700 transition-colors cursor-pointer"
                      >
                        Open Dockets →
                      </button>
                    </div>
                  )}

                  {/* Confirmed Booking Success Banner */}
                  {msg.confirmedBooking && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3.5 flex items-center justify-between text-xs font-ibm text-blue-950">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-accent-blue shrink-0" />
                        <div>
                          <div className="font-bold">Booking #{msg.confirmedBooking.inquiry_number}</div>
                          <div className="text-[11px] text-blue-700">Added to Pending Bookings queue</div>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          onClose();
                          navigate('/booking?status=PENDING');
                        }}
                        className="bg-accent-blue text-white font-bold px-3 py-1.5 rounded-lg hover:bg-accent-blue/90 transition-colors cursor-pointer"
                      >
                        Open Bookings →
                      </button>
                    </div>
                  )}

                  <span className="text-[10px] text-gray-400 block font-ibm px-1">
                    {msg.timestamp}
                  </span>
                </div>
              </div>
            </div>
          ))}

          {/* Analyzing loading animation */}
          {analyzing && (
            <div className="flex items-center gap-2.5 text-xs text-purple-700 font-ibm bg-purple-50/80 border border-purple-100 rounded-xl p-3 w-fit animate-pulse">
              <Loader2 className="w-4 h-4 animate-spin text-purple-600" />
              <span>Analyzing document with GPT-4o Vision & extracting parameters...</span>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* ─── Input & Upload Area ─── */}
        <div className="p-3.5 bg-white border-t border-gray-100 shrink-0 space-y-2">
          {/* Thumbnail preview if file attached */}
          {selectedFile && (
            <div className="flex items-center justify-between bg-purple-50/70 border border-purple-200 rounded-xl px-3 py-1.5 text-xs font-ibm">
              <div className="flex items-center gap-2 truncate">
                {previewImageUrl ? (
                  <img src={previewImageUrl} alt="thumb" className="w-8 h-8 rounded object-cover border border-purple-300" />
                ) : (
                  <FileText className="w-5 h-5 text-purple-600" />
                )}
                <span className="font-semibold text-purple-950 truncate max-w-xs">{selectedFile.name}</span>
                <span className="text-[10px] text-purple-600">({(selectedFile.size / 1024).toFixed(0)} KB)</span>
              </div>
              <button
                type="button"
                onClick={handleRemoveFile}
                className="text-purple-400 hover:text-purple-700 p-1 cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Chat input box */}
          <div className="flex items-end gap-2 bg-slate-50 border border-gray-200 rounded-2xl p-2 focus-within:ring-2 focus-within:ring-purple-500 focus-within:bg-white transition-all">
            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*,application/pdf"
              className="hidden"
            />

            {/* Attach button */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              title="Upload invoice screenshot, packing list or document"
              className="p-2 text-slate-500 hover:text-purple-700 hover:bg-purple-50 rounded-xl transition-colors cursor-pointer shrink-0"
            >
              <Paperclip className="w-4 h-4" />
            </button>

            {/* Prompt textarea */}
            <textarea
              ref={textareaRef}
              rows={1}
              value={inputPrompt}
              onChange={e => setInputPrompt(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              placeholder="Ask Copilot or paste screenshot (Ctrl+V) — e.g. 'Create docket for BioPharma Munich to Mumbai, 12 boxes, 240 kg'..."
              className="flex-1 bg-transparent border-0 resize-none text-xs font-ibm text-slate-800 placeholder:text-gray-400 focus:outline-none min-h-[36px] max-h-28 py-2 px-1"
            />

            {/* Send button */}
            <button
              type="button"
              onClick={() => handleSendMessage()}
              disabled={analyzing || (!inputPrompt.trim() && !selectedFile)}
              className="p-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-40 text-white rounded-xl shadow-xs transition-all cursor-pointer shrink-0"
            >
              {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
